import { Box, Text } from '@factorialco/deprecated-design-system'
import groupBy from 'lodash/groupBy'
import sum from 'lodash/sum'

import i18n from 'modules/core/lib/i18n'

import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type { ShiftsRow } from 'modules/shift_management/lib/types'
import type Shift from 'modules/shift_management/store/internal/models/Shift'
import { usePlannedBreaks } from 'modules/time_planning/store/internal/collections/plannedBreaks'
import type PlannedBreaks from 'modules/time_planning/store/internal/collections/plannedBreaks'

import HoursDisplay, { Element, formatDuration } from './HoursDisplay'

interface Props {
  shiftsRow: ShiftsRow
  isOverMaximum?: boolean
}

const calculateShiftsTotalDuration = (
  shifts: Shift[],
  plannedBreaksCollection: PlannedBreaks
): string => {
  return formatDuration(
    sum(
      shifts.map((shift) =>
        shift.durationWithBreaks(
          shift.plannedBreaks?.plannedBreaks ||
            // TODO: Remove this once 'dev_shift_management_graphql_migration' and
            // the mutations are done to use only shift.plannedBreaks
            plannedBreaksCollection.filterByShift(shift.id) ||
            []
        )
      )
    )
  )
}

interface ShiftSectionProps {
  title?: string | undefined
  totalDuration: string
  shiftEntries: Array<[string, Shift[]]>
  plannedBreaksCollection: PlannedBreaks
}

const ShiftSection = ({
  title,
  totalDuration,
  shiftEntries,
  plannedBreaksCollection,
}: ShiftSectionProps) => {
  return (
    <>
      {title && (
        <Box gap="s4" flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <Text size="200" weight="medium" color="grey1000">
            {title}
          </Text>

          <Box flexDirection="row" alignItems="center" gap="s8">
            <Text size="200" weight="medium" color="grey1000">
              {totalDuration}
            </Text>
          </Box>
        </Box>
      )}
      {shiftEntries.map(([shiftName, shifts]) => (
        <Element
          key={shiftName}
          title={`${shiftName} (${shifts.length})`}
          paddingLeft={title ? 's16' : undefined}
          value={calculateShiftsTotalDuration(shifts, plannedBreaksCollection)}
        />
      ))}
    </>
  )
}

const PlannedHours = ({ shiftsRow, isOverMaximum = false }: Props) => {
  const [{ view }] = useSchedule()
  // TODO: Remove this once 'dev_shift_management_graphql_migration' and
  // the mutations are done to use only shift.plannedBreaks
  const plannedBreaksCollection = usePlannedBreaks()

  const totalDuration = formatDuration(
    shiftsRow.hoursPerRangeAsMilliseconds(view.firstDay, view.lastDay, plannedBreaksCollection)
  )
  const shiftsByName = groupBy(
    shiftsRow.shiftsPerRange(view.firstDay, view.lastDay),
    (shift) => shift.name || i18n.t('shift_management.hours_information.unnamed_shift')
  )
  const sortedShiftEntries = Object.entries(shiftsByName).sort(([a], [b]) => a.localeCompare(b))

  const extraHoursShiftEntries = sortedShiftEntries.filter(([, shifts]) =>
    shifts.some((shift) => shift.isOvertime)
  )
  const regularShiftEntries = sortedShiftEntries.filter(([, shifts]) =>
    shifts.every((shift) => !shift.isOvertime)
  )

  const regularShifts = regularShiftEntries.flatMap(([, shifts]) => shifts)
  const regularHoursTotalDuration = calculateShiftsTotalDuration(
    regularShifts,
    plannedBreaksCollection
  )

  const extraHoursShifts = extraHoursShiftEntries.flatMap(([, shifts]) => shifts)
  const extraHoursTotalDuration = calculateShiftsTotalDuration(
    extraHoursShifts,
    plannedBreaksCollection
  )

  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const isComplementaryHoursEnabled = hasFeatureFlag('dev_time_tracking_complementary_hours')

  return (
    <HoursDisplay
      title={i18n.t('shift_management.hours_information.planned_hours.title')}
      totalDuration={totalDuration}
      hideBody={sortedShiftEntries.length === 0}
      triggerClassName={isOverMaximum ? 'text-f1-foreground-critical' : undefined}
    >
      {!isComplementaryHoursEnabled ? (
        <ShiftSection
          totalDuration={totalDuration}
          shiftEntries={sortedShiftEntries}
          plannedBreaksCollection={plannedBreaksCollection}
        />
      ) : (
        <>
          {regularShiftEntries.length > 0 && (
            <ShiftSection
              title={i18n.t('shift_management.hours_information.planned_hours.regular')}
              totalDuration={regularHoursTotalDuration}
              shiftEntries={regularShiftEntries}
              plannedBreaksCollection={plannedBreaksCollection}
            />
          )}
          {extraHoursShiftEntries.length > 0 && (
            <ShiftSection
              title={i18n.t('shift_management.hours_information.planned_hours.overtime')}
              totalDuration={extraHoursTotalDuration}
              shiftEntries={extraHoursShiftEntries}
              plannedBreaksCollection={plannedBreaksCollection}
            />
          )}
        </>
      )}
    </HoursDisplay>
  )
}

export default PlannedHours
